import { useFormik } from 'formik';
// mantine
import { Button, TextInput, UnstyledButton } from '@mantine/core';
// icons
import { RefreshCcw } from 'lucide-react';
// components
import { getBrowserTimezoneName } from '@newo/toolkit/datetime';
import { processFormData } from '@newo/toolkit/bff';
import { IActor } from '@newo/api/gateway';
import IntergrationFields from '@/modules/Integrations/components/IntegrationFields';
// utils
import { generateRandomString } from '@/utils';
import validationSchema from '@/modules/Sandbox/components/AddActorForm/AddActorForm.schema.ts';
import { IChatPersona } from '@/modules/Chat/Chat.types.ts';
import { useCreatePersonaActorMutation } from '@/services/customer/personas/personas.queries.ts';

interface AddActorFormProps {
    currentActor?: IActor;
    persona: IChatPersona;
    onClose: () => void;
    onSubmit: (actor: IActor) => void;
}

const EXTERNAL_ID_LENGTH = 6;

const AddActorForm = ({
    currentActor,
    persona,
    onClose,
    onSubmit
}: AddActorFormProps) => {
    const { mutate: createActor } = useCreatePersonaActorMutation();

    const initialValues = {
        name: persona.name,
        integration_idn: currentActor?.integration_idn ?? 'sandbox',
        connector_idn: currentActor?.connector_idn ?? '',
        external_id: generateRandomString(EXTERNAL_ID_LENGTH)
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const { name, integration_idn, connector_idn, external_id } =
                processFormData(formData);
            createActor(
                {
                    personaId: persona.id,
                    data: {
                        name,
                        integration_idn,
                        connector_idn,
                        external_id,
                        time_zone_identifier: getBrowserTimezoneName()
                    }
                },
                {
                    onSuccess: (newActor) => {
                        onSubmit({
                            name,
                            id: newActor.id,
                            integration_idn,
                            connector_idn,
                            external_id
                        } as IActor);
                        onClose();
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onClose();
    };

    const handleIntegrationChange = (value: string | null) => {
        formik.setFieldValue('integration_idn', value);
        formik.setFieldValue('connector_idn', undefined);
    };

    const handleConnectorChange = (value: string | null) => {
        formik.setFieldValue('connector_idn', value);
    };

    const handleAutogenerate = () => {
        formik.setFieldValue(
            'external_id',
            generateRandomString(EXTERNAL_ID_LENGTH)
        );
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <IntergrationFields
                connectorIdn={formik.values.connector_idn}
                errors={formik.errors}
                integrationIdn={formik.values.integration_idn}
                onConnectorChange={handleConnectorChange}
                onIntegrationChange={handleIntegrationChange}
            />
            <TextInput
                error={
                    formik.touched.external_id && formik.values.external_id
                        ? formik.errors.external_id
                        : undefined
                }
                label="External Id"
                name="external_id"
                placeholder="External Id"
                rightSection={
                    <UnstyledButton
                        title="Autogenerate"
                        onClick={handleAutogenerate}
                    >
                        <RefreshCcw />
                    </UnstyledButton>
                }
                value={formik.values.external_id}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={!formik.isValid || formik.isSubmitting}
                    size="xs"
                    type="submit"
                >
                    Create and apply
                </Button>
            </div>
        </form>
    );
};

export default AddActorForm;
